import React, { useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { getAuthMethods } from "../api/auth";
import { PENDING_INVITE_KEY } from "./Join";
import { EASE, SERIF } from "../components/marketing/primitives";

/**
 * Login — warm editorial, matching the marketing system: cream paper,
 * near-black ink, a white ink-bordered card, deep-green primary action.
 * No shadcn primitives — raw tailwind for full control over the card.
 */
export default function Login() {
  const { login, loginGoogle, user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const installed = searchParams.get("installed");
  const installationId = searchParams.get("installation_id");
  const showUpload = searchParams.get("upload") === "true";

  // Force light theme while mounted — match MarketingLayout behaviour.
  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains("dark");
    root.classList.remove("dark");
    return () => {
      if (wasDark) root.classList.add("dark");
    };
  }, []);

  useEffect(() => {
    if (user && !loading) {
      // An invitee who had to sign in first goes back to /join, not to the
      // dashboard — otherwise the invitation is silently dropped.
      const pendingInvite = sessionStorage.getItem(PENDING_INVITE_KEY);
      if (pendingInvite) {
        navigate(pendingInvite, { replace: true });
        return;
      }
      navigate(showUpload ? "/upload" : "/dashboard");
    }
  }, [user, loading, navigate, showUpload]);

  // Only offer a sign-in method this deployment can actually complete.
  const [methods, setMethods] = React.useState<{ github: boolean; google: boolean } | null>(null);
  useEffect(() => {
    let cancelled = false;
    getAuthMethods()
      .then((m) => !cancelled && setMethods(m))
      .catch(() => !cancelled && setMethods({ github: true, google: false }));
    return () => {
      cancelled = true;
    };
  }, []);
  const googleAvailable = methods?.google === true;

  return (
    <div className="relative min-h-screen overflow-x-clip bg-[#FBF8F1] text-[#17150F] antialiased">
      {/* Back-to-home — absolute so it doesn't push the card off-center */}
      <Link
        to="/"
        className="absolute left-6 top-6 z-20 inline-flex items-center gap-1.5 rounded-xl border border-[#17150F]/15 bg-white px-3.5 py-1.5 text-xs font-medium opacity-80 transition-all hover:-translate-y-0.5 hover:opacity-100"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to home
      </Link>

      <main className="relative z-10 flex min-h-screen items-center justify-center px-4 py-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: EASE }}
          className="w-full max-w-md"
        >
          {/* Logo row */}
          <div className="flex flex-col items-center gap-3 text-center">
            <Link to="/" className="flex items-center gap-2.5">
              <img src="/logo.png" alt="" className="h-10 w-10 rounded-lg" />
              <span className="flex flex-col items-start leading-tight">
                <span className="text-xl font-semibold tracking-tight">Paxley</span>
                <span className="text-[10px] font-semibold uppercase tracking-[0.16em] opacity-50">
                  Code security
                </span>
              </span>
            </Link>
            <h1 className="mt-3 text-3xl md:text-4xl" style={SERIF}>
              Welcome <em className="italic">back.</em>
            </h1>
            <p className="text-sm leading-relaxed opacity-60">Sign in to Paxley to continue.</p>
          </div>

          {/* Card */}
          <div className="mt-8 overflow-hidden rounded-2xl border border-[#17150F]/15 bg-white p-6 shadow-[0_16px_48px_-24px_rgba(23,21,15,0.3)] md:p-7">
            {/* Installation success */}
            {installed && installationId && (
              <div className="mb-5 flex items-center gap-3 rounded-xl border border-emerald-700/30 bg-emerald-50 p-3.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100">
                  <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-emerald-900">GitHub App installed</p>
                  <p className="text-xs text-emerald-800/70">Sign in to connect your account.</p>
                </div>
              </div>
            )}

            {/* OAuth buttons */}
            <div className="space-y-3">
              {googleAvailable && (
                <button
                  type="button"
                  onClick={loginGoogle}
                  disabled={loading}
                  className="flex h-11 w-full items-center justify-center gap-2.5 rounded-xl border border-[#17150F]/20 bg-white text-sm font-medium transition-all hover:-translate-y-0.5 hover:bg-[#FBF8F1] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </button>
              )}

              <button
                type="button"
                onClick={login}
                disabled={loading}
                className="group flex h-11 w-full items-center justify-center gap-2.5 rounded-xl border border-[#17150F] bg-[#1E4D38] text-sm font-semibold text-[#F5F1E6] transition-all hover:-translate-y-0.5 hover:bg-[#173D2C] hover:shadow-[0_6px_20px_-8px_rgba(23,21,15,0.5)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                  </svg>
                )}
                Continue with GitHub
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          </div>

          {/* Terms */}
          <p className="mt-6 text-center text-xs leading-relaxed opacity-50">
            By continuing, you agree to our{" "}
            <Link to="/terms" className="underline underline-offset-4 hover:opacity-100">
              Terms
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline underline-offset-4 hover:opacity-100">
              Privacy Policy
            </Link>
            .
          </p>
        </motion.div>
      </main>
    </div>
  );
}
